import {
  Breadcrumb,
  DefaultButton,
  getTheme,
  IBreadcrumbItem,
  Icon,
  mergeStyleSets,
  Pivot,
  PivotItem,
  PrimaryButton,
  Stack,
  Text,
} from '@fluentui/react';
import * as React from 'react';
import { navigateTo } from '../../../src/hooks';
import { Question } from '../../Question';
import { MsCertifiedBadge } from '../../MsCertifiedBadge';

const PRODUCT_URL = 'https://reenhanced.com/product/wp-connectr-pro/';
const QUOTE_URL = 'https://reenhanced.com/request-a-quote/';
const LOGO_URL = 'https://reenhanced.com/wp-content/uploads/2025/04/WPPowerPro-LOGO.png';
const SCREENSHOT_TRIGGER = 'https://reenhanced.com/wp-content/uploads/2025/04/pro-trigger-example.png';
const SCREENSHOT_DASHBOARD = 'https://reenhanced.com/wp-content/uploads/2025/04/Screenshot-2025-07-29-164546.png';
const SCREENSHOT_IMPORT = 'https://reenhanced.com/wp-content/uploads/2025/04/Screenshot-2025-07-23-105404.png';

const theme = getTheme();

const style = mergeStyleSets({
  container: {},
  heroBanner: {
    background: `linear-gradient(135deg, ${theme.palette.themeDarkAlt} 0%, ${theme.palette.themeDarker} 100%)`,
    borderRadius: 12, padding: '32px 36px', marginBottom: 28, color: '#fff',
  },
  heroLogoRow: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 },
  heroLogo: { height: 56, borderRadius: 10, objectFit: 'contain' as const, flexShrink: 0 },
  heroTitle: { ...theme.fonts.xxLarge, color: '#fff', margin: 0, fontWeight: 700 },
  heroSubtitle: { ...theme.fonts.mediumPlus, color: 'rgba(255,255,255,0.88)', marginBottom: 20, lineHeight: '1.6' },
  heroPricing: { ...theme.fonts.small, color: 'rgba(255,255,255,0.7)', marginTop: 8, display: 'block' as const },
  featuredPill: {
    display: 'inline-block', backgroundColor: '#f0a500', color: '#fff',
    padding: '3px 12px', borderRadius: 12, fontSize: 12, fontWeight: 700,
    marginBottom: 12, letterSpacing: 0.5,
  },
  trustRow: { display: 'flex', flexWrap: 'wrap' as const, gap: 10, marginBottom: 28 },
  trustPill: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    backgroundColor: theme.palette.neutralLighterAlt,
    border: `1px solid ${theme.palette.neutralLight}`,
    borderRadius: 20, padding: '5px 12px', fontSize: 13,
    color: theme.palette.neutralPrimary,
  },
  statGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginBottom: 28 },
  statBox: {
    display: 'flex', flexDirection: 'column' as const, alignItems: 'center',
    backgroundColor: theme.palette.neutralLighterAlt,
    border: `1px solid ${theme.palette.neutralLight}`,
    borderRadius: 8, padding: '16px 24px', textAlign: 'center' as const,
  },
  statValue: { ...theme.fonts.xxLarge, color: theme.palette.themePrimary, fontWeight: 700 },
  statLabel: { ...theme.fonts.small, color: theme.palette.neutralSecondary },
  sectionTitle: { ...theme.fonts.xLarge, fontWeight: 700, marginBottom: 16, marginTop: 24 },
  featureGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14, marginBottom: 24 },
  featureCard: {
    backgroundColor: theme.palette.neutralLighterAlt,
    border: `1px solid ${theme.palette.neutralLight}`,
    borderRadius: 8, padding: '14px 16px',
  },
  featureIcon: { color: theme.palette.themePrimary, fontSize: 20, marginBottom: 6, display: 'block' as const },
  featureTitle: { ...theme.fonts.medium, fontWeight: 700, marginBottom: 4 },
  featureText: { ...theme.fonts.small, color: theme.palette.neutralSecondary, lineHeight: '1.5' },
  screenshot: {
    width: '100%', maxWidth: 640, borderRadius: 8,
    border: `1px solid ${theme.palette.neutralLight}`,
    display: 'block' as const, margin: '16px 0',
  },
  useCaseList: { listStyle: 'none', paddingLeft: 0, margin: 0 },
  useCaseItem: { display: 'flex', gap: 12, padding: '12px 0', borderBottom: `1px solid ${theme.palette.neutralLighter}` },
  useCaseIcon: { color: theme.palette.themePrimary, fontSize: 20, flexShrink: 0, marginTop: 2 },
  useCaseTitle: { ...theme.fonts.medium, fontWeight: 700, marginBottom: 2 },
  useCaseDesc: { ...theme.fonts.small, color: theme.palette.neutralSecondary },
  comparisonTable: { width: '100%', borderCollapse: 'collapse' as const, marginTop: 16, fontSize: 14 },
  newBadge: {
    display: 'inline-block', backgroundColor: theme.palette.green, color: '#fff',
    padding: '1px 8px', borderRadius: 8, fontSize: 11, fontWeight: 700,
    marginLeft: 8, verticalAlign: 'middle',
  },
  securityBox: {
    backgroundColor: theme.palette.neutralLighterAlt,
    border: `1px solid ${theme.palette.neutralLight}`,
    borderRadius: 6, padding: '16px 20px', marginBottom: 16,
  },
  highlight: { backgroundColor: 'rgba(255, 245, 100, 0.65)', padding: '0 4px', borderRadius: 2 },
});

const tdStyle: React.CSSProperties = { padding: '10px 14px', border: '1px solid #ddd', verticalAlign: 'top' };
const thStyle: React.CSSProperties = { ...tdStyle, backgroundColor: '#f0f0f0', fontWeight: 700 };

const useCases = [
  { icon: 'Upload', title: 'Migrate Dataverse data to WordPress', desc: 'Export a Dataverse table to CSV and import 10,000+ records into WordPress with visual field mapping in hours instead of weeks.' },
  { icon: 'Sync', title: 'Real-time bidirectional sync', desc: 'Keep WordPress and your Power Platform environment perfectly aligned — changes flow both ways automatically.' },
  { icon: 'BuildDefinition', title: 'Auto-generate custom post types', desc: 'Upload a Power Platform solution and WP Connectr Pro automatically creates WordPress custom post types and ACF field groups.' },
  { icon: 'BulkUpload', title: 'Bulk-load legacy system data', desc: 'Import product catalogs, customer lists, or inventory data from any system that can export CSV — no per-record charges.' },
  { icon: 'ConditionalFormatting', title: 'Real-time event streaming', desc: 'Any WordPress event — post updates, user registrations, custom hooks — can trigger Power Automate flows instantly.' },
  { icon: 'Money', title: 'Eliminate custom development costs', desc: 'Replace six-figure custom integration projects with a subscription that delivers ROI in the first quarter.' },
];

export const WPConnectrPro: React.FC = () => {
  const breadcrumbs: IBreadcrumbItem[] = [
    { text: 'Products', key: 'products', onClick: (ev) => { ev.preventDefault(); navigateTo('/products'); } },
    { text: 'WP Connectr Pro', key: 'wpconnectrpro' },
  ];

  return (
    <>
      <Breadcrumb items={breadcrumbs} />
      <div className={style.container}>

        {/* ── Hero ── */}
        <div className={style.heroBanner}>
          <div className={style.featuredPill}>&#9733; Featured Product</div>
          <div className={style.heroLogoRow}>
            <img src={LOGO_URL} alt="WP Connectr Pro" className={style.heroLogo} />
            <h1 className={style.heroTitle}>WP Connectr Pro</h1>
          </div>
          <p className={style.heroSubtitle}>
            Transform WordPress into an <strong style={{ color: '#fff' }}>Enterprise Data Platform</strong>.
            Import Power Platform solutions, bulk-load massive datasets, and create real-time bidirectional
            sync between WordPress and the Microsoft Power Platform — all without writing a single line of code.
            Documented <strong style={{ color: '#fff' }}>80%+ cost savings</strong> vs custom development.
          </p>
          <Stack horizontal verticalAlign="center" wrap tokens={{ childrenGap: 12 }}>
            <PrimaryButton
              text="Get a Custom Quote"
              iconProps={{ iconName: 'Mail' }}
              onClick={() => { window.location.href = QUOTE_URL; }}
              styles={{ root: { backgroundColor: '#f0a500', borderColor: '#f0a500' } }}
            />
            <DefaultButton
              text="Learn More at reenhanced.com"
              iconProps={{ iconName: 'OpenInNewWindow' }}
              onClick={() => { window.location.href = PRODUCT_URL; }}
              styles={{ root: { color: '#fff', borderColor: 'rgba(255,255,255,0.6)', backgroundColor: 'transparent' } }}
            />
          </Stack>
          <div style={{ marginTop: 14 }}>
            <MsCertifiedBadge style={{ filter: 'brightness(2)' }} />
          </div>
          <span className={style.heroPricing}>
            From $1,500 / year &nbsp;&bull;&nbsp; Enterprise pricing available &nbsp;&bull;&nbsp; 80%+ projected cost savings
          </span>
        </div>

        {/* ── Stats ── */}
        <div className={style.statGrid}>
          {[
            { value: '43%', label: 'of all websites run on WordPress' },
            { value: '60,000+', label: 'WordPress plugins available' },
            { value: '80%+', label: 'Projected cost savings' },
            { value: 'Unlimited', label: 'CSV import size' },
          ].map((s, i) => (
            <div key={i} className={style.statBox}>
              <span className={style.statValue}>{s.value}</span>
              <span className={style.statLabel}>{s.label}</span>
            </div>
          ))}
        </div>

        {/* ── Trust signals ── */}
        <div className={style.trustRow}>
          {[
            { icon: 'Certificate', text: 'Microsoft Certified' },
            { icon: 'Code', text: 'No Code Required' },
            { icon: 'Upload', text: 'Solution Import' },
            { icon: 'BulkUpload', text: '10,000+ Record CSV Import' },
            { icon: 'Sync', text: 'Bidirectional Sync' },
            { icon: 'Lock', text: 'Enterprise Security' },
          ].map(t => (
            <span key={t.icon} className={style.trustPill}>
              <Icon iconName={t.icon} style={{ color: theme.palette.themePrimary }} />
              {t.text}
            </span>
          ))}
        </div>

        <Pivot>
          {/* ── Platform Features ── */}
          <PivotItem headerText="Platform Features">
            <Text block style={{ ...theme.fonts.mediumPlus as React.CSSProperties, lineHeight: '1.7', marginTop: 16, marginBottom: 20 }}>
              WP Connectr Pro 2.0 bridges Microsoft Power Platform and WordPress using the world's most
              trusted website platform. <span className={style.highlight}>Import your Dataverse solutions directly
              into WordPress</span>, bulk-load massive CSV datasets with intelligent field mapping,
              auto-generate custom post types with Advanced Custom Fields, and create real-time bidirectional
              sync — all leveraging WordPress's proven architecture trusted by 43% of all websites globally.
            </Text>

            <img src={SCREENSHOT_DASHBOARD} alt="WP Connectr Pro dashboard" className={style.screenshot} />

            <div className={style.sectionTitle}>
              Enterprise-Grade CSV Import <span className={style.newBadge}>NEW in 2.0</span>
            </div>
            <div className={style.featureGrid}>
              {[
                { icon: 'BulkUpload', title: '10,000+ Record Import', text: 'Bulk-load massive datasets from Dataverse or any CSV-exporting system with no file size limits.' },
                { icon: 'ColumnRightTwoThirdsEdit', title: 'Visual Field Mapping', text: 'Map CSV columns to ACF fields with a live data preview before committing the import.' },
                { icon: 'PausePresentation', title: 'Pause & Resume', text: 'Interrupt and resume imports without data loss — survives server timeouts and restarts.' },
                { icon: 'Processing', title: 'Intelligent Batch Processing', text: 'Automatic memory management prevents timeouts on large datasets, no matter the server configuration.' },
                { icon: 'MergeCell', title: 'Upsert Mode', text: 'Update existing records or create new ones with configurable conflict resolution — no duplicate records.' },
                { icon: 'ProgressRingDots', title: 'Real-Time Progress', text: 'Live tracking with detailed statistics and estimated completion time for every import job.' },
                { icon: 'ReportHacked', title: 'Comprehensive Error Logging', text: 'Detailed reports show exactly what succeeded and what failed, with exact line numbers for every issue.' },
                { icon: 'FieldRequired', title: 'All ACF Field Types', text: 'Text, date, select, checkbox, relationship, user, taxonomy — full support for every ACF field type.' },
              ].map(f => (
                <div key={f.icon} className={style.featureCard}>
                  <Icon iconName={f.icon} className={style.featureIcon} />
                  <div className={style.featureTitle}>{f.title}</div>
                  <div className={style.featureText}>{f.text}</div>
                </div>
              ))}
            </div>

            <img src={SCREENSHOT_IMPORT} alt="CSV import interface" className={style.screenshot} />

            <div className={style.sectionTitle}>Real-Time Data Synchronisation</div>
            <div className={style.featureGrid}>
              {[
                { icon: 'Sync', title: 'Bidirectional Sync', text: 'Sync Dataverse records to WordPress or use WordPress events to trigger Power Platform flows — both directions.' },
                { icon: 'Trigger', title: 'Comprehensive Triggers', text: 'Posts, pages, users, comments, custom post types, and more can all trigger Power Automate flows.' },
                { icon: 'People', title: 'User & Metadata Support', text: 'Registrations, profile updates, and custom user metadata are all available in Power Automate.' },
                { icon: 'Settings', title: 'WordPress Options Integration', text: 'Access and change site settings and configuration items through flows.' },
                { icon: 'Webhook', title: 'Enterprise Event Streaming', text: 'Real-time webhook delivery with retry logic and failure tracking for every event.' },
                { icon: 'DeveloperTools', title: 'Unlimited Extensibility', text: 'Any WordPress action hook can trigger flows — no limits on what can kick off an automation.' },
              ].map(f => (
                <div key={f.icon} className={style.featureCard}>
                  <Icon iconName={f.icon} className={style.featureIcon} />
                  <div className={style.featureTitle}>{f.title}</div>
                  <div className={style.featureText}>{f.text}</div>
                </div>
              ))}
            </div>

            <img src={SCREENSHOT_TRIGGER} alt="Trigger example in Power Automate" className={style.screenshot} />
          </PivotItem>

          {/* ── Use Cases ── */}
          <PivotItem headerText="Use Cases">
            <div className={style.sectionTitle} style={{ marginTop: 16 }}>What Enterprises Build With WP Connectr Pro</div>
            <ul className={style.useCaseList}>
              {useCases.map((uc, i) => (
                <li key={i} className={style.useCaseItem}>
                  <Icon iconName={uc.icon} className={style.useCaseIcon} />
                  <div>
                    <div className={style.useCaseTitle}>{uc.title}</div>
                    <div className={style.useCaseDesc}>{uc.desc}</div>
                  </div>
                </li>
              ))}
            </ul>
          </PivotItem>

          {/* ── Why WordPress ── */}
          <PivotItem headerText="Why WordPress">
            <Text block style={{ ...theme.fonts.mediumPlus as React.CSSProperties, lineHeight: '1.7', marginTop: 16, marginBottom: 20 }}>
              WordPress powers <strong>43% of all websites globally</strong> — including enterprise sites
              for Sony, The New York Times, and Microsoft itself. Its mature ecosystem of 60,000+ plugins
              delivers unlimited flexibility and the widest talent pool available. No customisation limits.
              No vendor lock-in. No complex per-user licensing.
            </Text>

            <table className={style.comparisonTable}>
              <thead>
                <tr>
                  <th style={thStyle}>Solution</th>
                  <th style={thStyle}>Implementation Cost</th>
                  <th style={thStyle}>Ongoing Cost</th>
                  <th style={thStyle}>Time to Value</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ ...tdStyle, fontWeight: 700, color: theme.palette.themePrimary }}>WP Connectr Pro</td>
                  <td style={tdStyle}>Implementation fee (contact for quote)</td>
                  <td style={tdStyle}>Subscription + hosting — still 80%+ savings</td>
                  <td style={tdStyle}>Hours to days</td>
                </tr>
                <tr>
                  <td style={tdStyle}>Custom Development</td>
                  <td style={tdStyle}>$50,000 – $200,000+</td>
                  <td style={tdStyle}>$5,000 – $15,000/month maintenance</td>
                  <td style={tdStyle}>6 – 18 months</td>
                </tr>
                <tr>
                  <td style={tdStyle}>Alternative Microsoft Website Provider</td>
                  <td style={tdStyle}>$25,000 – $100,000+</td>
                  <td style={tdStyle}>$2/website user/month + visitor fees</td>
                  <td style={tdStyle}>3 – 12 months</td>
                </tr>
              </tbody>
            </table>
          </PivotItem>

          {/* ── Security ── */}
          <PivotItem headerText="Security">
            <div className={style.securityBox} style={{ marginTop: 16 }}>
              <Text variant="large" block style={{ fontWeight: 'bold', marginBottom: 8 }}>
                <Icon iconName="Lock" /> Enterprise Security &amp; Compliance
              </Text>
              <ul>
                <li><strong>WordPress REST API Security</strong> — the same authentication system protecting 43% of the internet</li>
                <li><strong>SSL Encryption</strong> — all data transmission secured with enterprise-grade encryption</li>
                <li><strong>Role-Based Access Control</strong> — granular permissions using WordPress's proven capability system</li>
                <li><strong>Audit Trail</strong> — complete logging of all Power Platform interactions in WordPress logs</li>
                <li><strong>Microsoft Licensing Guidance</strong> — built-in guidance to ensure compliant Power Platform usage</li>
                <li><strong>Implementation Partnership</strong> — Reenhanced team works with you directly during deployment to ensure compliance</li>
              </ul>
            </div>
          </PivotItem>

          {/* ── FAQ ── */}
          <PivotItem headerText="FAQ">
            <Question question="How does solution import work?">
              <p>Upload your Power Platform solution (.zip file) and WP Connectr Pro automatically analyses the Dataverse tables, creates WordPress custom post types, and generates ACF field groups with proper data type mapping. No manual configuration required.</p>
            </Question>
            <Question question="What's new in version 2.0?">
              <p>Version 2.0 introduces enterprise-grade CSV import. Bulk-load 10,000+ records with visual field mapping, pause/resume functionality, and intelligent batch processing — handling datasets that would be impractical in Power Automate, with no file size limits.</p>
            </Question>
            <Question question="How does this compare to an alternative Microsoft website provider?">
              <p>Unlike providers with customisation limits, vendor lock-in, and data volume constraints, WordPress offers unlimited flexibility with 60,000+ plugins. You own your data, run on your servers, and avoid complex per-user licensing. Version 2.0's CSV import handles migrations that would be impossible on other platforms.</p>
            </Question>
            <Question question="Can this handle enterprise-scale deployments?">
              <p>Absolutely. WordPress powers enterprise sites for Sony, The New York Times, and Microsoft itself. WP Connectr Pro scales from single sites to enterprise WordPress networks, with enterprise-grade security and monitoring throughout.</p>
            </Question>
            <Question question="What are common use cases for the CSV import?">
              <p>Data migration from Dataverse, legacy system integration, bulk updates made offline in Excel, populating development environments, and importing product catalogs or customer lists from any external system.</p>
            </Question>
            <Question question="How quickly can we see ROI?">
              <p>Most organisations see immediate value from eliminating custom development costs. With documented 80%+ savings and implementation in days rather than months, ROI is typically realised in the first quarter. Version 2.0's CSV import alone can eliminate weeks of custom data migration work.</p>
            </Question>
            <Question question="What support is included?">
              <p>Enterprise customers receive dedicated implementation partnership with direct collaboration from our team. Contact us at <a href="mailto:support@reenhanced.com">support@reenhanced.com</a> or <a href={QUOTE_URL} target="_blank" rel="noreferrer">request a custom quote</a>.</p>
            </Question>
          </PivotItem>
        </Pivot>

        {/* ── Bottom CTA ── */}
        <div style={{
          background: `linear-gradient(135deg, ${theme.palette.themeDarkAlt} 0%, ${theme.palette.themeDarker} 100%)`,
          borderRadius: 10, padding: '28px 32px', marginTop: 24, color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap' as const, gap: 16,
        }}>
          <div>
            <Text block style={{ ...theme.fonts.large as React.CSSProperties, fontWeight: 700, marginBottom: 4, color: '#fff' }}>
              Ready to transform your business data strategy?
            </Text>
            <Text block style={{ color: 'rgba(255,255,255,0.8)' }}>
              From $1,500 / year &nbsp;&bull;&nbsp; Microsoft Certified &nbsp;&bull;&nbsp; 80%+ projected cost savings
            </Text>
          </div>
          <PrimaryButton
            text="Get a Custom Quote"
            iconProps={{ iconName: 'Mail' }}
            onClick={() => { window.location.href = QUOTE_URL; }}
            styles={{ root: { backgroundColor: '#f0a500', borderColor: '#f0a500' } }}
          />
        </div>

      </div>
    </>
  );
};

export default WPConnectrPro;
